import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Copy } from "lucide-react";
import { type RoomAllocation, type Occupancy, nightlyTotal, guestCapacity, totalRooms } from "@/lib/roomAllocations";

type Rate = { id: string; room_type: string; single_rate?: number | null; double_rate?: number | null; triple_rate?: number | null; currency?: string | null };

function rateFor(r: Rate | undefined, occ: Occupancy): number {
  if (!r) return 0;
  return Number(occ === "single" ? r.single_rate : occ === "triple" ? r.triple_rate || r.double_rate : r.double_rate) || 0;
}

interface Props {
  lines: RoomAllocation[];
  onChange: (lines: RoomAllocation[]) => void;
  rates: Rate[];
  nights: number;
  onCopyFromMain?: () => void;
}

export function RoomAllocationsEditor({ lines, onChange, rates, nights, onCopyFromMain }: Props) {
  const update = (i: number, patch: Partial<RoomAllocation>) =>
    onChange(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const addLine = () => {
    const r = rates[0];
    onChange([...lines, { room_rate_id: r?.id ?? null, room_type: r?.room_type ?? "", occupancy: "single", rooms: 1, rate: rateFor(r, "single"), currency: r?.currency ?? null }]);
  };

  const perNight = nightlyTotal(lines);

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label>Rooms on this stay</Label>
        <div className="flex flex-wrap gap-2">
          {onCopyFromMain && (
            <Button type="button" size="sm" variant="outline" onClick={onCopyFromMain}>
              <Copy className="mr-1 h-3.5 w-3.5" /> Copy room mix from main hotel
            </Button>
          )}
          <Button type="button" size="sm" variant="outline" onClick={addLine}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add room type
          </Button>
        </div>
      </div>

      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-2 gap-2 sm:grid-cols-[1.5fr_1fr_0.7fr_1fr_auto] sm:items-end">
          <div className="col-span-2 space-y-1 sm:col-span-1">
            <span className="text-xs text-muted-foreground">Room type</span>
            {rates.length > 0 ? (
              <Select
                value={l.room_rate_id ?? ""}
                onValueChange={(id) => {
                  const r = rates.find((x) => x.id === id);
                  update(i, { room_rate_id: id, room_type: r?.room_type ?? "", rate: rateFor(r, l.occupancy) || l.rate, currency: r?.currency ?? l.currency });
                }}
              >
                <SelectTrigger><SelectValue placeholder="Room type" /></SelectTrigger>
                <SelectContent>
                  {rates.map((r) => <SelectItem key={r.id} value={r.id}>{r.room_type}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={l.room_type} placeholder="e.g. Standard" onChange={(e) => update(i, { room_type: e.target.value })} />
            )}
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Occupancy</span>
            <Select
              value={l.occupancy}
              onValueChange={(o: Occupancy) => {
                const r = rates.find((x) => x.id === l.room_rate_id);
                update(i, { occupancy: o, rate: rateFor(r, o) || l.rate });
              }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single</SelectItem>
                <SelectItem value="double">Double</SelectItem>
                <SelectItem value="triple">Triple</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Rooms</span>
            <Input type="number" min={0} value={l.rooms} onChange={(e) => update(i, { rooms: Math.max(0, parseInt(e.target.value) || 0) })} />
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Rate / night</span>
            <Input type="number" min={0} step="0.01" value={l.rate} onChange={(e) => update(i, { rate: Math.max(0, parseFloat(e.target.value) || 0) })} />
          </div>
          <Button type="button" size="icon" variant="ghost" aria-label="Remove room line" onClick={() => onChange(lines.filter((_, idx) => idx !== i))}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <p className="text-xs text-muted-foreground">
        {totalRooms(lines)} rooms · {guestCapacity(lines)} guests · {perNight.toLocaleString()} per night
        {nights > 0 && <> · {(perNight * nights).toLocaleString()} for {nights} night{nights === 1 ? "" : "s"}</>}
      </p>
    </div>
  );
}
